import { Fragment, useMemo } from 'react';
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useEffortsDesignationTrend } from '../hooks/useEffortsDashboard';
import { formatNumber } from '../../../utils/dashboardFormatters';
import {
  FIRST_COL_SX,
  HEADER_CELL_SX,
  NUM_CELL_SX,
  UNAVAIL_CELL_SX,
  TABLE_CARD_SX,
  TABLE_SX,
  TABLE_WRAP_SX,
  TableCardTitle,
} from './EffortsTableParts.jsx';
import {
  buildFyMonths,
  covTextColor,
  compTextColor,
} from './effortsTableStyles.js';

const DASH = '—';
const UNAVAIL_TIP = 'Data not available yet';

/**
 * Designation groups and their KPI rows, verbatim from the reference
 * `ROWS`/`KPI_LBL`. FLS carries all eight KPIs; the manager tiers carry five.
 * Each KPI is tagged with the backend field it reads, or `available:false`
 * when the efforts designation-trend payload does NOT carry it (Leaves,
 * Non Field Activity Days, Dr. Missed) — those render a greyed em dash.
 */
const KPI_DEFS = {
  emp: { label: 'No. of Active Emp', field: 'flsCount', kind: 'int', available: true },
  wd: { label: 'No. of Avg. W. Days', field: 'avgWorkingDays', kind: 'num1', available: true },
  // Leaves + Non Field Activity Days arrive null from the backend → greyed em dash.
  lv: { label: 'No. of Leaves', field: 'noOfLeaves', kind: 'num1', available: false },
  nf: { label: 'No. of Non Field Activity Days', field: 'noOfNonFieldActivityDays', kind: 'num1', available: false },
  dr: { label: 'Dr. Call Avg.', field: 'drCallAverage', kind: 'num2', available: true },
  cov: { label: 'Dr. Coverage %', field: 'coveragePercent', kind: 'pct', available: true },
  comp: { label: 'Freq. compliance %', field: 'compliancePercent', kind: 'pct', available: true },
  // Dr. Missed is now backend-sourced (drMissed) for every role.
  miss: { label: 'No. of Dr. Missed', field: 'drMissed', kind: 'int', available: true },
};

/** [designation, kpiKeys[]] rows, reference `ROWS`. */
const ROWS = [
  ['FLS', ['emp', 'wd', 'lv', 'nf', 'dr', 'cov', 'comp', 'miss']],
  ['FLM', ['emp', 'wd', 'lv', 'nf', 'dr']],
  ['SLM', ['emp', 'wd', 'lv', 'nf', 'dr']],
  ['TLM', ['emp', 'wd', 'lv', 'nf', 'dr']],
];

/** Format a KPI value by its kind; null → em dash. */
function fmtKpi(value, kind) {
  if (value == null || !Number.isFinite(Number(value))) return DASH;
  const n = Number(value);
  if (kind === 'int') return formatNumber(n);
  if (kind === 'num2') return n.toFixed(2);
  if (kind === 'pct') return `${n.toFixed(1)}%`;
  return n.toFixed(1); // num1
}

export default function DesignationTrendTab() {
  const { data, isLoading, isError } = useEffortsDesignationTrend();

  const series = useMemo(() => data?.series ?? [], [data]);

  // Always render the full FY (Apr→Mar); no-data months still show as columns.
  const months = useMemo(
    () => buildFyMonths(data?.fiscalYear, data?.months),
    [data?.fiscalYear, data?.months]
  );

  // Index each designation's points by yearmonth for O(1) cell lookup. The
  // series key is the role/designation (`designation` or `role` from the
  // backend); match on whichever is present.
  const byDesig = useMemo(() => {
    const map = {};
    series.forEach((s) => {
      const key = s.designation ?? s.role;
      if (key == null) return;
      map[key] = Object.fromEntries(
        (s.points ?? []).map((p) => [String(p.yearmonth), p])
      );
    });
    return map;
  }, [series]);

  if (isLoading) return <Alert severity="info">Loading trend…</Alert>;
  if (isError) return <Alert severity="error">Failed to load designation trend.</Alert>;
  if (series.length === 0) return <Alert severity="info">No trend data available.</Alert>;

  return (
    <Box>
      <Box sx={TABLE_CARD_SX}>
        <TableCardTitle title="Effort Trend — by Designation &amp; Month (Apr→Mar)" />
        <Box sx={{ fontSize: '12px', color: '#6B7280', mb: 1.5, mt: -0.5 }}>
          Month-on-month effort by designation. Reacts to the sales filters (via
          MIS HQ); shows all 12 months regardless of the Period selector.
          Coverage %, Compliance % and Dr. Missed are FLS-only.
        </Box>
        <TableContainer sx={TABLE_WRAP_SX}>
          <Table size="small" sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell sx={HEADER_CELL_SX}>Designation</TableCell>
                <TableCell sx={HEADER_CELL_SX}>KPI</TableCell>
                {months.map((m) => (
                  <TableCell key={m.yearmonth} align="right" sx={HEADER_CELL_SX}>
                    {m.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {ROWS.map(([desig, kpiKeys]) => (
                <Fragment key={desig}>
                  {kpiKeys.map((kpiKey, rowIndex) => {
                    const def = KPI_DEFS[kpiKey];
                    const points = byDesig[desig] ?? {};
                    return (
                      <TableRow
                        key={`${desig}-${kpiKey}`}
                        sx={rowIndex === 0 ? { '& td': { borderTop: '2px solid #e2e8f2' } } : undefined}
                      >
                        {rowIndex === 0 ? (
                          <TableCell rowSpan={kpiKeys.length} sx={FIRST_COL_SX}>
                            {desig}
                          </TableCell>
                        ) : null}
                        {def.available ? (
                          <TableCell>{def.label}</TableCell>
                        ) : (
                          <Tooltip title={UNAVAIL_TIP}>
                            <TableCell sx={{ color: '#aeb6c2' }}>{def.label}</TableCell>
                          </Tooltip>
                        )}
                        {months.map((m) => {
                          if (!def.available) {
                            return (
                              <TableCell key={m.yearmonth} align="right" sx={UNAVAIL_CELL_SX}>
                                {DASH}
                              </TableCell>
                            );
                          }
                          const raw = points[m.yearmonth]?.[def.field];
                          const text = fmtKpi(raw, def.kind);
                          // Coverage / compliance use coloured TEXT (not cells).
                          const color =
                            kpiKey === 'cov'
                              ? covTextColor(raw)
                              : kpiKey === 'comp'
                                ? compTextColor(raw)
                                : undefined;
                          return (
                            <TableCell
                              key={m.yearmonth}
                              align="right"
                              sx={{
                                ...NUM_CELL_SX,
                                ...(color ? { color, fontWeight: 800 } : {}),
                              }}
                            >
                              {text}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}
